import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFilterModel, IPaginationModel, ISortModel } from '@/types/admin/dataTable';

export interface TableUIState {
  searchQuery: string;
  filterModel: IFilterModel;
  sortModel: ISortModel[];
  paginationModel: IPaginationModel;
}

export interface TableState {
  // key: tableKey (e.g., 'admin:userManagement')
  [key: string]: TableUIState | undefined;
}

const DEFAULT_TABLE_STATE: TableUIState = {
  searchQuery: '',
  filterModel: { items: [] },
  sortModel: [],
  paginationModel: { page: 0, pageSize: 25 },
};

const initialState: TableState = {};

type Keyed<T> = T & { key: string };

const tableStateSlice = createSlice({
  name: 'tableState',
  initialState,
  reducers: {
    initializeTable: (state, action: PayloadAction<Keyed<Partial<TableUIState>>>) => {
      const { key, ...initial } = action.payload;
      if (!state[key]) {
        state[key] = {
          ...DEFAULT_TABLE_STATE,
          ...(initial.searchQuery != null ? { searchQuery: initial.searchQuery } : {}),
          ...(initial.filterModel ? { filterModel: initial.filterModel } : {}),
          ...(initial.sortModel ? { sortModel: initial.sortModel } : {}),
          ...(initial.paginationModel ? { paginationModel: initial.paginationModel } : {}),
        };
      }
    },
    resetTable: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      state[key] = { ...DEFAULT_TABLE_STATE };
    },
    setSearchQuery: (state, action: PayloadAction<{ key: string; searchQuery: string }>) => {
      const { key, searchQuery } = action.payload;
      state[key] = { ...(state[key] || DEFAULT_TABLE_STATE), searchQuery };
    },
    setFilterModel: (state, action: PayloadAction<{ key: string; filterModel: IFilterModel }>) => {
      const { key, filterModel } = action.payload;
      state[key] = { ...(state[key] || DEFAULT_TABLE_STATE), filterModel };
    },
    setSortModel: (state, action: PayloadAction<{ key: string; sortModel: ISortModel[] }>) => {
      const { key, sortModel } = action.payload;
      state[key] = { ...(state[key] || DEFAULT_TABLE_STATE), sortModel };
    },
    setPaginationModel: (state, action: PayloadAction<{ key: string; paginationModel: IPaginationModel }>) => {
      const { key, paginationModel } = action.payload;
      state[key] = { ...(state[key] || DEFAULT_TABLE_STATE), paginationModel };
    },
  },
});

export const {
  initializeTable,
  resetTable,
  setSearchQuery,
  setFilterModel,
  setSortModel,
  setPaginationModel,
} = tableStateSlice.actions;

export default tableStateSlice.reducer;

export const DEFAULTS = DEFAULT_TABLE_STATE;

